// Task #3898 — Cards hub tab in Financials.
//
// Lists all Mercury IO Credit Cards linked to this host's account with their
// assignment info, status, and per-card actions (create / freeze / unfreeze /
// cancel / assign-property).
//
// The cancel action requires the host to type "cancel" in a confirmation modal
// before the irreversible Mercury API call is made.
//
// Gated: Growth+ AND `mercury_card_provisioning` beta. Only shown when the host
// has an active Mercury connection with at least one linked card.

import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { parseErrorMessage } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Snowflake, Sun, X, CreditCard, RefreshCw, AlertTriangle, Plus, MapPin, ChevronRight, ChevronDown, Receipt } from "lucide-react";
import { cn } from "@/lib/utils";

interface EnrichedCard {
  id: number;
  mercuryCardId: string;
  nickname: string | null;
  brand: string | null;
  last4: string | null;
  expMonth: number | null;
  expYear: number | null;
  mercuryCardStatus: string | null;
  assignedPropertyId: number | null;
  assignedPropertyName: string | null;
  assignedPropertyStatus: string | null;
  monthlySpend?: number | null;
}

interface CardTxn {
  id: string;
  amount: number;
  date: string;
  vendor: string | null;
  category: string | null;
  description: string | null;
}

function formatMoney(n: number | null | undefined): string {
  return `$${Number(n ?? 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

// Local YYYY-MM-DD helpers for the transaction date-range inputs (default to the
// current calendar month, month-start through today).
function ymd(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function monthStartStr(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
}
function todayStr(): string {
  return ymd(new Date());
}

interface Property {
  id: number;
  name: string;
  status: string | null;
}

export function StatusBadge({ status }: { status: string | null }) {
  const s = (status ?? "unknown").toLowerCase();
  if (s === "active") {
    return <Badge className="bg-green-100 text-green-800 border-green-200" variant="outline" data-testid="badge-card-active">Active</Badge>;
  }
  if (s === "frozen") {
    return <Badge className="bg-blue-100 text-blue-800 border-blue-200" variant="outline" data-testid="badge-card-frozen">Frozen</Badge>;
  }
  if (s === "cancelled" || s === "canceled") {
    return <Badge className="bg-red-100 text-red-800 border-red-200" variant="outline" data-testid="badge-card-cancelled">Cancelled</Badge>;
  }
  if (s === "inactive" || s === "expired") {
    return <Badge className="bg-gray-100 text-gray-600 border-gray-200" variant="outline" data-testid="badge-card-inactive">{s === "expired" ? "Expired" : "Inactive"}</Badge>;
  }
  return <Badge variant="outline" data-testid="badge-card-unknown">{status ?? "Unknown"}</Badge>;
}

// ─── Cancel confirmation modal ────────────────────────────────────────────────

interface CancelConfirmModalProps {
  card: EnrichedCard | null;
  onClose: () => void;
  onConfirm: (card: EnrichedCard) => void;
  isPending: boolean;
}

function CancelConfirmModal({ card, onClose, onConfirm, isPending }: CancelConfirmModalProps) {
  const [confirmText, setConfirmText] = useState("");
  const canConfirm = confirmText.trim().toLowerCase() === "cancel";

  function handleClose() {
    if (isPending) return;
    setConfirmText("");
    onClose();
  }

  function handleConfirm() {
    if (!card || !canConfirm || isPending) return;
    onConfirm(card);
  }

  return (
    <Dialog
      open={!!card}
      onOpenChange={(open) => { if (!open) handleClose(); }}
    >
      <DialogContent
        onInteractOutside={(e) => { if (isPending) e.preventDefault(); }}
        onEscapeKeyDown={(e) => { if (isPending) e.preventDefault(); }}
        onPointerDownOutside={(e) => { if (isPending) e.preventDefault(); }}
        data-testid="modal-cancel-card"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-700">
            <AlertTriangle className="w-5 h-5" />
            Cancel Mercury Card
          </DialogTitle>
          <DialogDescription>
            This will permanently cancel{" "}
            <strong>{card?.nickname ?? `Card •••• ${card?.last4 ?? "****"}`}</strong>{" "}
            in Mercury. This action is <strong>irreversible</strong> — the card cannot be
            reinstated. Any linked property assignment will be removed.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
            <p className="font-medium mb-1">Before cancelling, make sure:</p>
            <ul className="list-disc list-inside space-y-1 text-red-700">
              <li>No recurring charges are pending on this card</li>
              <li>You have updated any vendor accounts using this card number</li>
              <li>The associated property has an alternative payment method</li>
            </ul>
          </div>
          <div className="space-y-2">
            <Label htmlFor="cancel-confirm-input" className="font-medium">
              Type <span className="font-mono bg-gray-100 px-1 rounded">cancel</span> to confirm
            </Label>
            <Input
              id="cancel-confirm-input"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="cancel"
              disabled={isPending}
              autoComplete="off"
              data-testid="input-cancel-confirm"
            />
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={isPending}
            data-testid="button-cancel-dismiss"
          >
            Keep Card
          </Button>
          <Button
            variant="destructive"
            onClick={handleConfirm}
            disabled={!canConfirm || isPending}
            data-testid="button-cancel-confirm"
          >
            {isPending ? "Cancelling…" : "Cancel Card Permanently"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Create card dialog ───────────────────────────────────────────────────────

interface CreateCardDialogProps {
  open: boolean;
  onClose: () => void;
  properties: Property[];
  isPending: boolean;
  onConfirm: (propertyId: number) => void;
}

function CreateCardDialog({ open, onClose, properties, isPending, onConfirm }: CreateCardDialogProps) {
  const [selectedPropertyId, setSelectedPropertyId] = useState<string>("");
  const activeProperties = properties.filter((p) => !["archived", "sold", "disposed"].includes(p.status ?? ""));

  function handleClose() {
    if (isPending) return;
    setSelectedPropertyId("");
    onClose();
  }

  function handleConfirm() {
    const id = parseInt(selectedPropertyId, 10);
    if (!id || isPending) return;
    onConfirm(id);
  }

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) handleClose(); }}>
      <DialogContent
        onInteractOutside={(e) => { if (isPending) e.preventDefault(); }}
        onEscapeKeyDown={(e) => { if (isPending) e.preventDefault(); }}
        onPointerDownOutside={(e) => { if (isPending) e.preventDefault(); }}
        data-testid="modal-create-card"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-brand" />
            Create Mercury Card
          </DialogTitle>
          <DialogDescription>
            Provision a new Mercury IO Credit Card for a property. The card will be named
            after the property and assigned to it automatically.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="create-card-property-select" className="font-medium">Property</Label>
            {activeProperties.length === 0 ? (
              <p className="text-sm text-gray-500 py-2">No active properties found. Add a property first.</p>
            ) : (
              <Select value={selectedPropertyId} onValueChange={setSelectedPropertyId} disabled={isPending}>
                <SelectTrigger id="create-card-property-select" data-testid="select-create-card-property">
                  <SelectValue placeholder="Select a property…" />
                </SelectTrigger>
                <SelectContent>
                  {activeProperties.map((p) => (
                    <SelectItem key={p.id} value={String(p.id)} data-testid={`option-property-${p.id}`}>
                      {p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
          <p className="text-xs text-gray-500">
            Mercury will create a virtual credit card under your IO Credit Card account.
            This operation is real and cannot be undone from PadPulse — cancel the card
            to deactivate it.
          </p>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={handleClose} disabled={isPending} data-testid="button-create-card-cancel">
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!selectedPropertyId || isPending}
            data-testid="button-create-card-confirm"
          >
            {isPending ? "Creating…" : "Create Card"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Assign property dialog ───────────────────────────────────────────────────

interface AssignPropertyDialogProps {
  card: EnrichedCard | null;
  onClose: () => void;
  properties: Property[];
  isPending: boolean;
  onConfirm: (card: EnrichedCard, propertyId: number) => void;
}

function AssignPropertyDialog({ card, onClose, properties, isPending, onConfirm }: AssignPropertyDialogProps) {
  const [selectedPropertyId, setSelectedPropertyId] = useState<string>("");
  const activeProperties = properties.filter((p) => !["archived", "sold", "disposed"].includes(p.status ?? ""));

  function handleClose() {
    if (isPending) return;
    setSelectedPropertyId("");
    onClose();
  }

  function handleConfirm() {
    const id = parseInt(selectedPropertyId, 10);
    if (!id || !card || isPending) return;
    onConfirm(card, id);
  }

  return (
    <Dialog open={!!card} onOpenChange={(o) => { if (!o) handleClose(); }}>
      <DialogContent
        onInteractOutside={(e) => { if (isPending) e.preventDefault(); }}
        onEscapeKeyDown={(e) => { if (isPending) e.preventDefault(); }}
        onPointerDownOutside={(e) => { if (isPending) e.preventDefault(); }}
        data-testid="modal-assign-card"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MapPin className="w-5 h-5" />
            {card?.assignedPropertyId ? "Reassign" : "Assign"} Card to Property
          </DialogTitle>
          <DialogDescription>
            Choose which property{" "}
            <strong>{card?.nickname ?? `Card •••• ${card?.last4 ?? "****"}`}</strong>{" "}
            should be linked to.
            {card?.assignedPropertyName && (
              <> Currently assigned to <strong>{card.assignedPropertyName}</strong>.</>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="assign-card-property-select" className="font-medium">Property</Label>
            {activeProperties.length === 0 ? (
              <p className="text-sm text-gray-500 py-2">No active properties found.</p>
            ) : (
              <Select value={selectedPropertyId} onValueChange={setSelectedPropertyId} disabled={isPending}>
                <SelectTrigger id="assign-card-property-select" data-testid="select-assign-card-property">
                  <SelectValue placeholder="Select a property…" />
                </SelectTrigger>
                <SelectContent>
                  {activeProperties.map((p) => (
                    <SelectItem key={p.id} value={String(p.id)} data-testid={`option-assign-property-${p.id}`}>
                      {p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={handleClose} disabled={isPending} data-testid="button-assign-cancel">
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!selectedPropertyId || isPending}
            data-testid="button-assign-confirm"
          >
            {isPending ? "Assigning…" : "Assign Property"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Expandable per-card transaction list ─────────────────────────────────────
// Rendered inline beneath a card row when expanded. Fetches live Mercury
// transactions for the card via the server proxy, filtered to a date range
// (defaults to month-to-date). Shows loading, error, and empty states plus a
// running spend total for the selected window.
function CardTransactions({ card }: { card: EnrichedCard }) {
  const [start, setStart] = useState(monthStartStr());
  const [end, setEnd] = useState(todayStr());

  const txnQuery = useQuery<{ transactions: CardTxn[]; rangeSpend: number }>({
    queryKey: ["/api/app/mercury/cards", card.mercuryCardId, "transactions", start, end],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (start) params.set("start", start);
      if (end) params.set("end", end);
      const res = await apiRequest(
        "GET",
        `/api/app/mercury/cards/${card.mercuryCardId}/transactions?${params.toString()}`,
      );
      return res.json();
    },
    retry: false,
  });

  const txns = txnQuery.data?.transactions ?? [];

  return (
    <div className="border-t border-gray-100 bg-gray-50/60 px-4 py-4" data-testid={`card-transactions-${card.id}`}>
      <div className="flex flex-wrap items-end gap-3 mb-3">
        <div className="space-y-1">
          <Label htmlFor={`txn-start-${card.id}`} className="text-xs text-gray-500">From</Label>
          <Input
            id={`txn-start-${card.id}`}
            type="date"
            value={start}
            max={end || undefined}
            onChange={(e) => setStart(e.target.value)}
            className="h-8 w-[150px]"
            data-testid={`input-txn-start-${card.id}`}
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor={`txn-end-${card.id}`} className="text-xs text-gray-500">To</Label>
          <Input
            id={`txn-end-${card.id}`}
            type="date"
            value={end}
            min={start || undefined}
            onChange={(e) => setEnd(e.target.value)}
            className="h-8 w-[150px]"
            data-testid={`input-txn-end-${card.id}`}
          />
        </div>
        <div className="ml-auto text-right">
          <p className="text-xs text-gray-500">Spend in range</p>
          <p className="text-sm font-semibold text-gray-900" data-testid={`text-txn-range-spend-${card.id}`}>
            {formatMoney(txnQuery.data?.rangeSpend ?? 0)}
          </p>
        </div>
      </div>

      {txnQuery.isLoading ? (
        <div className="space-y-2" data-testid={`txn-loading-${card.id}`}>
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-9 rounded-md bg-gray-100 animate-pulse" />
          ))}
        </div>
      ) : txnQuery.isError ? (
        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-3 text-sm text-red-700" data-testid={`txn-error-${card.id}`}>
          {parseErrorMessage(txnQuery.error, "Couldn't load transactions. Try again.")}
        </div>
      ) : txns.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-6 text-center text-gray-400" data-testid={`txn-empty-${card.id}`}>
          <Receipt className="w-6 h-6 mb-2 text-gray-300" />
          <p className="text-sm">No transactions in this date range.</p>
        </div>
      ) : (
        <div className="divide-y divide-gray-100 rounded-md border border-gray-200 bg-white" data-testid={`txn-list-${card.id}`}>
          {txns.map((t) => {
            const isCredit = t.amount < 0;
            return (
              <div key={t.id} className="flex items-center gap-3 px-3 py-2" data-testid={`txn-row-${card.id}-${t.id}`}>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-gray-800 truncate">
                    {t.vendor ?? t.description ?? "Transaction"}
                  </p>
                  <p className="text-xs text-gray-500 flex items-center gap-2 flex-wrap">
                    <span>{t.date.slice(0, 10)}</span>
                    {t.category && <span className="text-gray-400">· {t.category}</span>}
                  </p>
                </div>
                <span
                  className={cn(
                    "text-sm font-semibold shrink-0 tabular-nums",
                    isCredit ? "text-green-600" : "text-gray-900",
                  )}
                  data-testid={`txn-amount-${card.id}-${t.id}`}
                >
                  {isCredit ? "+" : ""}{formatMoney(Math.abs(t.amount))}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function CardsTab() {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [cardToCancel, setCardToCancel] = useState<EnrichedCard | null>(null);
  const [cardToAssign, setCardToAssign] = useState<EnrichedCard | null>(null);
  const [showCreateCard, setShowCreateCard] = useState(false);
  const [expandedCardId, setExpandedCardId] = useState<number | null>(null);

  const cardsQuery = useQuery<{ cards: EnrichedCard[] }>({
    queryKey: ["/api/app/mercury/cards"],
  });

  const propertiesQuery = useQuery<Property[]>({
    queryKey: ["/api/app/properties"],
  });

  const creditBalanceQuery = useQuery<{ totalCurrentBalance: string; totalAvailableBalance: string; accountCount: number }>({
    queryKey: ["/api/app/mercury/credit-balance"],
    retry: false,
  });

  const freezeMutation = useMutation({
    mutationFn: (cardId: string) =>
      apiRequest("POST", `/api/app/mercury/cards/${cardId}/freeze`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/app/mercury/cards"] });
      toast({ title: "Card frozen", description: "Spending is suspended until you unfreeze." });
    },
    onError: (err) => {
      toast({ title: "Failed to freeze card", description: parseErrorMessage(err, "Mercury returned an error. Try again."), variant: "destructive" });
    },
  });

  const unfreezeMutation = useMutation({
    mutationFn: (cardId: string) =>
      apiRequest("POST", `/api/app/mercury/cards/${cardId}/unfreeze`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/app/mercury/cards"] });
      toast({ title: "Card unfrozen", description: "Spending is re-enabled." });
    },
    onError: (err) => {
      toast({ title: "Failed to unfreeze card", description: parseErrorMessage(err, "Mercury returned an error. Try again."), variant: "destructive" });
    },
  });

  const cancelMutation = useMutation({
    mutationFn: (card: EnrichedCard) =>
      apiRequest("POST", `/api/app/mercury/cards/${card.mercuryCardId}/cancel`, { cardId: card.id }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/app/mercury/cards"] });
      qc.invalidateQueries({ queryKey: ["/api/app/mercury/cards/status"] });
      setCardToCancel(null);
      toast({ title: "Card cancelled", description: "The Mercury card has been permanently cancelled." });
    },
    onError: (err) => {
      toast({ title: "Failed to cancel card", description: parseErrorMessage(err, "Mercury returned an error. Try again."), variant: "destructive" });
    },
  });

  const syncMutation = useMutation({
    meta: { silentSave: true },
    mutationFn: () => apiRequest("POST", "/api/app/mercury/cards/sync").then((r) => r.json()),
    onSuccess: (data: any) => {
      qc.invalidateQueries({ queryKey: ["/api/app/mercury/cards"] });
      qc.invalidateQueries({ queryKey: ["/api/app/mercury/cards/status"] });
      qc.invalidateQueries({ queryKey: ["/api/app/mercury/credit-balance"] });
      const txnNote = data?.txnsIngested ? ` ${data.txnsIngested} transaction(s) ingested.` : "";
      toast({ title: "Cards synced", description: `${data?.fetched ?? 0} card(s) pulled from Mercury.${txnNote}` });
    },
    onError: (err) => {
      toast({ title: "Sync failed", description: parseErrorMessage(err, "Could not sync cards from Mercury."), variant: "destructive" });
    },
  });

  const createCardMutation = useMutation({
    mutationFn: (propertyId: number) =>
      apiRequest("POST", "/api/app/mercury/cards", { propertyId }).then((r) => r.json()),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/app/mercury/cards"] });
      qc.invalidateQueries({ queryKey: ["/api/app/mercury/cards/status"] });
      setShowCreateCard(false);
      toast({ title: "Card created", description: "A new Mercury card has been provisioned and linked to the property." });
    },
    onError: (err) => {
      toast({ title: "Failed to create card", description: parseErrorMessage(err, "Mercury returned an error. Try again."), variant: "destructive" });
    },
  });

  const assignMutation = useMutation({
    mutationFn: ({ mercuryCardId, propertyId }: { mercuryCardId: string; propertyId: number }) =>
      apiRequest("POST", "/api/app/mercury/cards/assign", { mercuryCardId, propertyId }).then((r) => r.json()),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/app/mercury/cards"] });
      setCardToAssign(null);
      toast({ title: "Card assigned", description: "The card has been linked to the selected property." });
    },
    onError: (err) => {
      toast({ title: "Failed to assign card", description: parseErrorMessage(err, "Try again."), variant: "destructive" });
    },
  });

  const cards = cardsQuery.data?.cards ?? [];
  const properties = propertiesQuery.data ?? [];
  const isLoading = cardsQuery.isLoading;
  const anyMutationPending =
    freezeMutation.isPending ||
    unfreezeMutation.isPending ||
    cancelMutation.isPending ||
    assignMutation.isPending;

  if (isLoading) {
    return (
      <div className="space-y-3 py-8">
        {[1, 2, 3].map((n) => (
          <div key={n} className="h-16 rounded-lg bg-gray-100 animate-pulse" />
        ))}
      </div>
    );
  }

  const creditBalance = creditBalanceQuery.data;

  return (
    <div className="space-y-4" data-testid="cards-hub-tab">
      {creditBalance && creditBalance.accountCount > 0 && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 flex items-center justify-between" data-testid="credit-balance-banner">
          <div>
            <p className="text-sm font-medium text-amber-900">Credit cards owed</p>
            <p className="text-xs text-amber-700 mt-0.5">Outstanding balance across {creditBalance.accountCount} Mercury IO Credit account{creditBalance.accountCount !== 1 ? "s" : ""}</p>
          </div>
          <div className="text-right">
            <p className="text-lg font-semibold text-amber-900" data-testid="text-credit-balance-current">
              ${Number(creditBalance.totalCurrentBalance).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </p>
            <p className="text-xs text-amber-600">current balance</p>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Mercury Cards</h2>
          <p className="text-sm text-gray-500">
            Virtual credit cards issued via Mercury IO Credit Card — one per property.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => syncMutation.mutate()}
            disabled={syncMutation.isPending}
            data-testid="button-sync-cards"
          >
            <RefreshCw className={cn("w-4 h-4 mr-2", syncMutation.isPending && "animate-spin")} />
            {syncMutation.isPending ? "Syncing…" : "Sync Cards"}
          </Button>
          <Button
            size="sm"
            onClick={() => setShowCreateCard(true)}
            disabled={syncMutation.isPending || createCardMutation.isPending}
            data-testid="button-open-create-card"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Card
          </Button>
        </div>
      </div>

      {cards.length === 0 ? (
        <div className="rounded-xl border border-dashed border-gray-300 py-16 text-center text-gray-500" data-testid="cards-empty-state">
          <CreditCard className="w-10 h-10 mx-auto mb-3 text-gray-300" />
          <p className="font-medium text-gray-700">No cards linked yet</p>
          <p className="text-sm mt-1">
            Use <strong>Sync Cards</strong> to pull your Mercury IO Credit Cards, or{" "}
            <button
              className="underline text-brand hover:text-brand/80"
              onClick={() => setShowCreateCard(true)}
              data-testid="link-create-card-empty"
            >
              create a new card
            </button>{" "}
            for a property.
          </p>
        </div>
      ) : (
        <div className="rounded-xl border border-gray-200 divide-y divide-gray-100" data-testid="cards-list">
          {cards.map((card) => {
            const status = (card.mercuryCardStatus ?? "unknown").toLowerCase();
            const isCancelled = status === "cancelled" || status === "canceled";
            const isFrozen = status === "frozen";
            const isActive = status === "active";
            const isFreezeable = isActive;
            const isUnfreezeable = isFrozen;
            const isExpanded = expandedCardId === card.id;

            return (
              <div key={card.id} data-testid={`card-row-${card.id}`}>
              <div
                className={cn(
                  "flex items-center gap-3 px-4 py-3",
                  isCancelled && "opacity-60",
                )}
              >
                <button
                  type="button"
                  onClick={() => setExpandedCardId(isExpanded ? null : card.id)}
                  className="shrink-0 rounded-md p-1 text-gray-400 hover:text-gray-700 hover:bg-gray-100"
                  aria-label={isExpanded ? "Hide transactions" : "Show transactions"}
                  aria-expanded={isExpanded}
                  data-testid={`button-expand-${card.id}`}
                >
                  {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                </button>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-sm truncate" data-testid={`text-card-name-${card.id}`}>
                      {card.nickname ?? `Card •••• ${card.last4 ?? "****"}`}
                    </span>
                    {card.last4 && (
                      <span className="text-xs text-gray-500" data-testid={`text-card-last4-${card.id}`}>
                        •••• {card.last4}
                      </span>
                    )}
                    <StatusBadge status={card.mercuryCardStatus} />
                  </div>
                  <div className="text-xs text-gray-500 mt-0.5 flex items-center gap-2 flex-wrap">
                    {card.assignedPropertyName ? (
                      <span data-testid={`text-card-property-${card.id}`}>
                        Assigned to: <span className="font-medium text-gray-700">{card.assignedPropertyName}</span>
                      </span>
                    ) : (
                      <span className="italic text-gray-400" data-testid={`text-card-unassigned-${card.id}`}>No property assigned</span>
                    )}
                    {card.expMonth && card.expYear && (
                      <span>
                        Exp {card.expMonth.toString().padStart(2, "0")}/{card.expYear}
                      </span>
                    )}
                  </div>
                </div>

                <div className="text-right shrink-0">
                  <p className="text-xs text-gray-500">This month</p>
                  <p className="text-sm font-semibold text-gray-900" data-testid={`text-card-monthly-spend-${card.id}`}>
                    {formatMoney(card.monthlySpend ?? 0)}
                  </p>
                </div>

                {!isCancelled && (
                  <div className="flex items-center gap-2 shrink-0 flex-wrap justify-end">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCardToAssign(card)}
                      disabled={anyMutationPending}
                      data-testid={`button-assign-${card.id}`}
                      title={card.assignedPropertyId ? "Reassign to a different property" : "Assign to a property"}
                    >
                      <MapPin className="w-4 h-4 mr-1.5 text-gray-500" />
                      {card.assignedPropertyId ? "Reassign" : "Assign"}
                    </Button>
                    {isFreezeable && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => freezeMutation.mutate(card.mercuryCardId)}
                        disabled={anyMutationPending}
                        data-testid={`button-freeze-${card.id}`}
                        title="Freeze card"
                      >
                        <Snowflake className="w-4 h-4 mr-1.5 text-blue-500" />
                        Freeze
                      </Button>
                    )}
                    {isUnfreezeable && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => unfreezeMutation.mutate(card.mercuryCardId)}
                        disabled={anyMutationPending}
                        data-testid={`button-unfreeze-${card.id}`}
                        title="Unfreeze card"
                      >
                        <Sun className="w-4 h-4 mr-1.5 text-amber-500" />
                        Unfreeze
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setCardToCancel(card)}
                      disabled={anyMutationPending}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      data-testid={`button-open-cancel-${card.id}`}
                      title="Cancel card permanently"
                    >
                      <X className="w-4 h-4 mr-1.5" />
                      Cancel
                    </Button>
                  </div>
                )}
              </div>
              {isExpanded && <CardTransactions card={card} />}
              </div>
            );
          })}
        </div>
      )}

      <CancelConfirmModal
        card={cardToCancel}
        onClose={() => setCardToCancel(null)}
        onConfirm={(card) => cancelMutation.mutate(card)}
        isPending={cancelMutation.isPending}
      />

      <CreateCardDialog
        open={showCreateCard}
        onClose={() => setShowCreateCard(false)}
        properties={properties}
        isPending={createCardMutation.isPending}
        onConfirm={(propertyId) => createCardMutation.mutate(propertyId)}
      />

      <AssignPropertyDialog
        card={cardToAssign}
        onClose={() => setCardToAssign(null)}
        properties={properties}
        isPending={assignMutation.isPending}
        onConfirm={(card, propertyId) => assignMutation.mutate({ mercuryCardId: card.mercuryCardId, propertyId })}
      />
    </div>
  );
}
